import { Icon, Icons } from '@Components/icon/Icon';
import { ControllerWrapper } from '@Hooks/index';
import { UboResponse } from '@Store/index';
import { OnboardingFormFields } from '@Types/types';
import {
  FontWeight,
  Heading,
  LiliButton,
  LiliButtonType,
  LiliCheckbox,
  Text,
} from '@lili/lili-frontend-common';
import { Control, UseFormGetValues, UseFormTrigger } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { OwnersCenterFormType } from '../OwnersCenterForm/OwnersCenterForm.types';
import { UboItem } from './UboItem';

export interface UBOManagementProps {
  control: Control<OwnersCenterFormType>;
  getValues: UseFormGetValues<OwnersCenterFormType>;
  uboList: UboResponse[];
  onEditClick: (uboId: string) => void;
  onAddClick: () => void;
  refetchBusinessOwners: () => void;
  hasMoreThan25PercentOwnershipLeft: boolean;
  trigger: UseFormTrigger<OwnersCenterFormType>;
}

export const UBOManagement = (props: UBOManagementProps) => {
  const {
    control,
    getValues,
    uboList,
    onEditClick,
    onAddClick,
    refetchBusinessOwners,
    hasMoreThan25PercentOwnershipLeft,
    trigger,
  } = props;
  const { t } = useTranslation();

  const handleConcentChange = (value: boolean, onChange: (value: boolean) => void) => {
    onChange(value);
    trigger();
  };

  return (
    <>
      <div className='mt-[22px] mb-[16px]'>
        <Heading.H9 fontWeight={FontWeight.SEMIBOLD} className='mb-[8px] '>
          {t('pages.ownersCenter.secondaryTitle')}
        </Heading.H9>
        <Text.H9 className='text-text-main-secondary'>
          {t('pages.ownersCenter.secondaryContent')}
        </Text.H9>
      </div>
      {uboList.map((ubo, index) => (
        <UboItem
          key={ubo.externalId}
          index={index}
          ubo={ubo}
          onEditClick={onEditClick}
          refetchBusinessOwners={refetchBusinessOwners}
        />
      ))}
      {uboList.length < 4 && (
        <div className='flex sm:items-center items-start gap-[24px] md:gap-[32px] flex-col sm:flex-row  justify-between'>
          {hasMoreThan25PercentOwnershipLeft && (
            <div className='w-full'>
              <LiliButton
                id='elBtnAddOwner'
                type={LiliButtonType.SECONDARY}
                onClick={onAddClick}
                title={t('pages.ownersCenter.addOwner')}
                className={`${uboList.length === 0 ? 'w-full md:max-w-[240px]' : 'w-full'}`}
                icon={<Icon icon={Icons.Plus} className='fill-text-brand-primary' />}
                disabled={getValues(OnboardingFormFields.OWNER_CENTER_CONSENT_ONLY_UBO)}
              />
            </div>
          )}
          {uboList.length === 0 && (
            <div className='w-full md:ml-[54px]'>
              <ControllerWrapper
                control={control}
                name={OnboardingFormFields.OWNER_CENTER_CONSENT_ONLY_UBO}>
                {({ value, onChange }) => (
                  <div
                    className='flex flex-row w-full items-start gap-[8px]'
                    onClick={(newVal) => handleConcentChange(!newVal, onChange)}>
                    <LiliCheckbox
                      containerClassName=' translate-y-[2px]'
                      id='elPSingleUbo'
                      checked={value}
                      onCheckedChange={(newVal) => handleConcentChange(newVal, onChange)}
                    />
                    <Text.H10 className='text-text-main-primary'>
                      {t('pages.ownersCenter.onlyUbo')}
                    </Text.H10>
                  </div>
                )}
              </ControllerWrapper>
            </div>
          )}
        </div>
      )}
      {uboList.length > 0 && (
        <div className='w-full mt-[24px]'>
          <ControllerWrapper
            control={control}
            name={OnboardingFormFields.OWNER_CENTER_MULTI_OWNER_CONSENT}>
            {({ value, onChange }) => (
              <div
                className='flex flex-row w-full  items-start gap-[8px] cursor-pointer'
                onClick={(newVal) => handleConcentChange(!newVal, onChange)}>
                <LiliCheckbox
                  id='elCheckbox'
                  checked={value}
                  onCheckedChange={(newVal) => handleConcentChange(newVal, onChange)}
                  containerClassName='mt-[2px]'
                />
                <Text.H10 className='text-text-main-secondary'>
                  {t('pages.ownersCenter.concent')}
                </Text.H10>
              </div>
            )}
          </ControllerWrapper>
        </div>
      )}
    </>
  );
};
